import { DataGrid } from '@mui/x-data-grid'
import translate from '../../../i18n/translate'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import RobotSmsCreditsModal from '../../Modals/RobotSmsCreditsModal'
import useStyles from './useStyles'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import SettingsIcon from '@mui/icons-material/Settings'
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Button, Typography, IconButton } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { organizerApi } from '../../../modules/services/organizerService'
import { format } from 'date-fns'
import Filter from '../../UI/Filter'
import OrganizerSettingsModal from '../../Modals/OrganizerSettingsModal'
import { useNavigate } from 'react-router-dom'
import OrganizerSettingsEventFeePercentModal from '../../Modals/OrganizerSettingsEventFeePercentModal'
import OrganizerFilesModal, { OrganizerFile } from '../../Modals/OrganizerFilesModal'
import CreateTransactionModal from '../../Modals/CreateTransactionModal'

type Props = {
  type?: string
  count: number
  organizerId?: number
  setCount: (v: number) => void
}

const OrganizersDataGrid = (props: Props) => {
  const { type, count, organizerId, setCount } = props

  const takeCount = 10
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const navigate = useNavigate();
  const [columns, setColumns] = useState<any[]>([])
  const [rows, setRows] = useState<any[]>([])
  const [currentCredits, setCurrentCredits] = useState<any | null>(null)
  const [currentOrganizer, setCurrentOrganizer] = useState<any | null>(null)
  const [currentOrganizerFiles, setCurrentOrganizerFiles] = useState<any | null>(null)
  const [currentOrganizerSettings, setCurrentOrganizerSettings] = useState<any | null>(null)
  const [filterRequestString, setFilterRequestString] = useState<
    string | undefined
  >(undefined)
  const [changedProfileID, setChangedProfileID] = useState<any | undefined>(
    undefined
  )
  //#region API requests
  const [organizersDataLazyFetch] = organizerApi.useLazyFetchOrganizersQuery()
  const {
    refetch: organizersDataRefetch,
    data: organizersData,
    isSuccess: isOrganizersSuccess,
    isFetching: isOrganizersFetching
  } = organizerApi.useFetchOrganizersQuery({ type, organizerId })

  const [
    updateOrganizerRobotSmsAccess,
    { isLoading: isUpdateOrganizerRobotSmsAccess }
  ] = organizerApi.useUpdateRobotSmsAccessMutation()
  const [
    changeOrganizerAutoConfirmation,
    { isLoading: isAutoConfirmationLoading }
  ] = organizerApi.useAutoConfirmationMutation()
  const [
    updateOrganizerRobotSmsCredits,
    { isLoading: isUpdateOrganizerRobotSmsCredits }
  ] = organizerApi.useUpdateRobotSmsCreditsMutation()
  const [openAsAdminLazy] = organizerApi.useLazyOpenOrganizerAsAdminQuery()

  const { toggleRobotSmsCreditsModal, toggleOrganizerSettingsModal, toggleOrganizerFilesModal, toggleOrganizerSettingsEventFeePercentModal, toggleCreateTransactionModal } = modalsSlice.actions

  const handleOpenOrganizerSettingsModal = (row: any) => {
    dispatch(toggleOrganizerSettingsModal());
    setCurrentOrganizer({ userId: row.id, ...row.organizerInfo })
  };

  const handleOpenCreateTransactionModal = (row: any) => {
    dispatch(toggleCreateTransactionModal());
    setCurrentOrganizer({ userId: row.id, ...row.organizerInfo })
  };

  const visibleTransactionModal = useAppSelector(state => state.modalsReducer.createTransaction)
  

  const handleOpenOrganizerFilesModal = (row: any) => {
    dispatch(toggleOrganizerFilesModal());
    setCurrentOrganizerFiles({ userId: row.id, phone: row.phone, files: row?.organizerFiles || [] })
  };

  const handleOpenOrganizerSettingsEventFeeModal = (row: any) => {
    dispatch(toggleOrganizerSettingsEventFeePercentModal());
    setCurrentOrganizerSettings({ id: row.id, ...row.organizerSettingsInfo })
  };
  //#endregion

  //#region Handlers
  function onOpenAsAdmin (id: number) {
    openAsAdminLazy({ id: id }).then(response => {
      window.open((response.data as any).link)
    })
  }

  function handleUpdateSuccess(userId: number, data: {
      isEarlyWithdrawalEnabled: boolean;
      earlyWithdrawalFee?: number | null;
      earlyWithdrawalDays?: number | null;
      earlyWithdrawalPercent?: number | null;
      freeTransactionCount: number;
      transactionMinSum: Number;
      transactionFee: number;
      fastWithdrawalFee: number;
      withdrawalType: string;
      withdrawalDays?: number | null;
      adminNote?: string | null;
  }) {
    setRows((prev) => {
      return prev.map((row) => {
        if (row.id === userId) {
          return {
            ...row,
            organizerInfo: {
              ...row.organizerInfo,
              ...data,
            }
          }
        }

        return row;
      })
    })
  }

  const handleDeleteFileSuccess = (userId: number, fileId: number) => {
    setRows((prev) => {
      return prev.map((row) => {
        if (row.id === userId) {
          return {
            ...row,
            organizerFiles: row.organizerFiles.filter((f: any) => f.id !== fileId)
          }
        }

        return row;
      })
    })

    setCurrentOrganizerFiles((prev: any) => ({ ...prev, files: prev.files.filter((f: any) => f.id !== fileId) }))
  }

  const handleUploadFileSuccess = (userId: number, file: OrganizerFile) => {
    setRows((prev) => {
      return prev.map((row) => {
        if (row.id === userId) {
          return {
            ...row,
            organizerFiles: [...row.organizerFiles, file]
          }
        }

        return row;
      })
    })

    setCurrentOrganizerFiles((prev: any) => ({ ...prev, files: [...prev.files, file] }))
  }

  function handleUpdateSettingsSuccess(id: number, data: {
    feePercent: number;
  }) {
    setRows((prev) => {
      return prev.map((row) => {
        if (row.id === id) {
          return {
            ...row,
            organizerSettingsInfo: {
            ...row.organizerSettingsInfo,
              ...data,
            }
          }
        }

        return row;
      })
    })
  }

  function handleChangeOrganizerRobotSmsAccess (clickedRow: any) {
    setChangedProfileID(clickedRow.id)

    const updatedOrganizerData = rows.map((x: any) => {
      if (x.id === clickedRow.id) {
        return {
          ...x,
          isCanUseRobotSms: !x.isCanUseRobotSms
        }
      }

      return x
    })

    updateOrganizerRobotSmsAccess({
      id: clickedRow.id,
      isCanUseRobotSms: !clickedRow.isCanUseRobotSms
    })
      .unwrap()
      .then((result: any) => {
        setRows(updatedOrganizerData)
        setChangedProfileID(undefined)
      })
      .catch((error: any) => console.error('ERROR', error))
  }

  function handleChangeOrganizerAutoConfirmation (clickedRow: any) {
    setChangedProfileID(clickedRow.id)

    const updatedOrganizerData = rows.map((x: any) => {
      if (x.id === clickedRow.id) {
        return {
          ...x,
          isAutoConfirmation: !x.isAutoConfirmation
        }
      }

      return x
    })

    changeOrganizerAutoConfirmation(clickedRow.id)
      .unwrap()
      .then((result: any) => {
        setRows(updatedOrganizerData)
        setChangedProfileID(undefined)
      })
      .catch((error: any) => console.error('ERROR', error))
  }

  function handleChangeOrganizerRobotSmsCredits (credits: number) {
    const updatedOrganizerData = rows.map((x: any) => {
      if (x.id === changedProfileID) {
        return {
          ...x,
          robotSmsCredits: credits
        }
      }

      return x
    })

    updateOrganizerRobotSmsCredits({
      id: changedProfileID,
      robotSmsCredits: credits
    })
      .unwrap()
      .then((result: any) => {
        setRows(updatedOrganizerData)
        setChangedProfileID(undefined)
        setCurrentCredits(0)
      })
      .catch((error: any) => toast.error(translate('updateCreditsError')))
  }

  function handleOpenChangeOrganizerRobotSmsCreditsModal (
    id: number | string,
    credits: number
  ) {
    setChangedProfileID(id)
    setCurrentCredits(credits)

    dispatch(toggleRobotSmsCreditsModal())
  }

  function onLoadMoreClick (filter: string | undefined) {
    organizersDataLazyFetch({
      type: type,
      skip: rows.length,
      take: takeCount,
      filter: filter
    })
      .then((response: any) => {
        const result = rows.concat(response.data.list)

        if (rows.length + takeCount > 80) {
          result.splice(0, takeCount)
          toast.success(translate('hideRows'))
        }

        setRows(result)
        setCount(response.data.totalCount)
      })
      .catch((errors: any) => console.error(errors))
  }

  function onFilterClick (filter: string | undefined) {
    organizersDataLazyFetch({
      type: type,
      filter: filter
    })
      .then((response: any) => {
        setRows(response.data.list)
        setCount(response.data.totalCount)
      })
      .catch((errors: any) => console.error(errors))
  }
  //#endregion

  const showTransactions = (id: number) => {
    navigate(`/organizers/${id}/transaction/list/all`)
  }

  //#region UseEffect`s
  useEffect(() => {
    if (isOrganizersSuccess) {
      setRows(organizersData.list)
      setCount(organizersData.totalCount)
    }
  }, [organizersData, isOrganizersSuccess, setCount])

  useEffect(() => {
    const organizerColumn = {
      field: 'organizerInfo',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridOrganizersOrganizerInfo'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.OrganizerCell}>
            <Typography>
              <span>{translate('gridOrganizersName')}</span> {params.row.name}{' '}
              {params.row.surname}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersPhone')}</span> {params.row.phone}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersEmail')}</span>{' '}
              {params.row.email ?? '-'}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersIDcode')}</span>{' '}
              {params.row.IDcode ?? '-'}
            </Typography>
            {params.row.organizerInfo?.adminNote ? (
              <Typography>
                <span>{translate('organizerSettingsAdminNote')}:</span>
                <br />
                {params.row.organizerInfo?.adminNote ?? '-'}
              </Typography>
            ) : null}
          </Box>
        )
      }
    }

    const organizerEventsColumn = {
      field: 'organizerEventsInfo',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridOrganizersOrganizerEventsInfo'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.OrganizerCell}>
            <Typography>
              <span>{translate('gridOrganizersTotalEventsCount')}</span>{' '}
              {params.row.eventsCount}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersActiveEventsCount')}</span>{' '}
              {params.row.activeEventsCount}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersFinishedEventsCount')}</span>{' '}
              {params.row.finishedEventsCount}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersCancelledEventsCount')}</span>{' '}
              {params.row.cancelledEventsCount}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersPendingEventsCount')}</span>{' '}
              {params.row.pendingEventsCount}
            </Typography>
          </Box>
        )
      }
    }

    const organizerEventsActivityColumn = {
      field: 'organizerEventsActivityInfo',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridOrganizersOrganizerEventsActivityInfo'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.OrganizerCell}>
            <Typography>
              <span>{translate('gridOrganizersTotalTicketsSoldCount')}</span>{' '}
              {params.row.totalTicketSoldCount}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersTotalTicketsSoldMoney')}</span>{' '}
              {params.row.totalTicketsSoldMoney}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersTotalTicketsSoldFee')}</span>{' '}
              {params.row.totalTicketSoldFee}
            </Typography>
            <Typography>
              <span>
                {translate('gridOrganizersActivePeriodTotalTicketsSoldCount')}
              </span>{' '}
              {params.row.activePeriodTotalTicketSoldCount}
            </Typography>
            <Typography>
              <span>
                {translate('gridOrganizersActivePeriodTotalTicketsSoldMoney')}
              </span>{' '}
              {params.row.activePeriodTotalTicketsSoldMoney}
            </Typography>
            <Typography>
              <span>
                {translate('gridOrganizersActivePeriodTotalTicketsSoldFee')}
              </span>{' '}
              {params.row.activePeriodTotalTicketSoldFee}
            </Typography>
          </Box>
        )
      }
    }

    const organizerActivityColumn = {
      field: 'organizerActivityColumn',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridOrganizersOrganizerActivityInfo'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.OrganizerCell}>
            <Typography>
              <span>{translate('gridOrganizersJoinDate')}</span>{' '}
              {params.row.createDate
                ? format(new Date(params.row.createDate), 'dd/MM/yyyy HH:mm')
                : '-'}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersLastEventDate')}</span>{' '}
              {params.row.lastEventDate
                ? format(new Date(params.row.lastEventDate), 'dd/MM/yyyy HH:mm')
                : '-'}
            </Typography>
            <Typography>
              <span>{translate('gridOrganizersStatus')}</span>{' '}
              <b>
                {params.row.isActive ? (
                  <span style={{ color: 'green' }}>
                    {translate('gridOrganizersStatusActive')}
                  </span>
                ) : (
                  <span style={{ color: 'grey' }}>
                    {translate('gridOrganizersStatusNotActive')}
                  </span>
                )}
              </b>
            </Typography>
          </Box>
        )
      }
    }

    let selectedColumns = [
      organizerColumn,
      organizerEventsColumn,
      organizerEventsActivityColumn,
      organizerActivityColumn
    ]

    selectedColumns.push({
      field: 'settings',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridOrganizersSettings'),
      renderCell: (params: any) => {
        return (
          <>
            <Box className={classes.OrganizerCell}>
              <Box display='flex' gap={2}>
                <IconButton
                  sx={{
                    color: '#fc7f04'
                  }}
                  onClick={() => handleOpenOrganizerSettingsModal(params.row)}
                >
                  <SettingsIcon />
                </IconButton>
                <IconButton
                  sx={{
                    color: '#fc7f04'
                  }}
                  onClick={() => handleOpenOrganizerSettingsEventFeeModal(params.row)}
                >
                  <PriceChangeIcon />
                </IconButton>
                <IconButton
                  sx={{
                    color: '#fc7f04'
                  }}
                  onClick={() => handleOpenOrganizerFilesModal(params.row)}
                >
                  <AttachFileIcon />
                </IconButton>
                <IconButton
                  sx={{
                    color: '#fc7f04'
                  }}
                  onClick={() => handleOpenCreateTransactionModal(params.row)}
                >
                  <LocalAtmIcon />
                </IconButton>
              </Box>
              <Box my={0.5}>
                <Button
                  size='small'
                  color='warning'
                  onClick={() => showTransactions(params.row.id)}
                >
                  {translate('showTransactions')}
                </Button>
              </Box>
              <Box>
                <Button
                  size='small'
                  color='warning'
                  onClick={() => onOpenAsAdmin(params.row.id)}
                >
                  {translate('gridEventsOpenAsOwner')}
                </Button>
              </Box>
              {params.row.isCanUseRobotSms && (
                <Box mt={'10px'}>
                  <Box mt={'5px'}>
                    <Typography fontSize={14}>
                      <span>
                        {translate('gridOrganizersChangeRobotSmsCreditsCount')}
                      </span>{' '}
                      {params.row.robotSmsCredits}
                    </Typography>
                  </Box>
                  <Box mt={'5px'}>
                    <Button
                      disabled={
                        isUpdateOrganizerRobotSmsCredits &&
                        params.row.id === changedProfileID
                      }
                      size='small'
                      color='warning'
                      onClick={() =>
                        handleOpenChangeOrganizerRobotSmsCreditsModal(
                          params.row.id,
                          params.row.robotSmsCredits
                        )
                      }
                    >
                      {translate('gridOrganizersChangeRobotSmsCredits')}
                    </Button>
                  </Box>
                </Box>
              )}
              <Button
                sx={{
                  color: '#fc7f04'
                }}
                disabled={isUpdateOrganizerRobotSmsAccess}
                endIcon={
                  isUpdateOrganizerRobotSmsAccess &&
                  params.row.id === changedProfileID ? (
                    <CircularProgress size={20} />
                  ) : (params.row.isCanUseRobotSms as boolean) ? (
                    <CheckBoxIcon style={{ marginRight: '10px' }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon style={{ marginRight: '10px' }} />
                  )
                }
                onClick={() => handleChangeOrganizerRobotSmsAccess(params.row)}
              >
                {translate('gridOrganizersChangeRobotSmsAccess')}
              </Button>
              <Button
                sx={{
                  color: '#fc7f04'
                }}
                disabled={isAutoConfirmationLoading}
                endIcon={
                  isAutoConfirmationLoading &&
                  params.row.id === changedProfileID ? (
                    <CircularProgress size={20} />
                  ) : (params.row.isAutoConfirmation as boolean) ? (
                    <CheckBoxIcon style={{ marginRight: '10px' }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon style={{ marginRight: '10px' }} />
                  )
                }
                onClick={() =>
                  handleChangeOrganizerAutoConfirmation(params.row)
                }
              >
                {translate('gridOrganizersAutoConfirmationUser')}
              </Button>
            </Box>
          </>
        )
      }
    } as any)

    setColumns(selectedColumns)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  useEffect(() => {
    organizersDataRefetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, organizerId])
  //#endregion

  if (isOrganizersFetching)
    return (
      <Box
        sx={{
          height: '400px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress color='warning' />
      </Box>
    )

  return (
    <>
      <RobotSmsCreditsModal
        handleChange={(credits: number) =>
          handleChangeOrganizerRobotSmsCredits(credits)
        }
        robotSmsCredits={currentCredits}
      />
      {currentOrganizer && (
        <OrganizerSettingsModal key={currentOrganizer.id} organizerInfo={currentOrganizer} handleUpdateSuccess={handleUpdateSuccess} />
      )}
      {currentOrganizer && visibleTransactionModal === 'open' && (
        <CreateTransactionModal key={currentOrganizer.id} organizerInfo={currentOrganizer} />
      )}
      {currentOrganizerSettings && (
        <OrganizerSettingsEventFeePercentModal key={currentOrganizerSettings} organizerSettingsInfo={currentOrganizerSettings} handleUpdateSuccess={handleUpdateSettingsSuccess} />
      )}
      {currentOrganizerFiles && (
        <OrganizerFilesModal key={currentOrganizerFiles} organizerFiles={currentOrganizerFiles} handleDeleteSuccess={handleDeleteFileSuccess} handleUploadSuccess={handleUploadFileSuccess} />
      )}

      <Toaster position='bottom-right' toastOptions={{ duration: 4000 }} />
      <div
        style={{
          position: 'absolute',
          top: '0',
          width: '100%',
          paddingBottom: '70px'
        }}
      >
        <Filter
          setFilterRequestString={setFilterRequestString}
          fetchRequest={onFilterClick}
          onlyPaidField={true}
          sortByNameField={true}
        />
        <DataGrid
          sx={{
            backgroundColor: '#ede9e4',
            borderRadius: '0px',
            border: 'none',

            '& .MuiDataGrid-virtualScroller': {
              marginTop: '0 !important'
            },
            '& .MuiDataGrid-main': {
              overflow: 'visible'
            },
            '.MuiDataGrid-columnHeaders': {
              backgroundColor: '#fc7f03',
              position: 'sticky',
              zIndex: 2,
              borderRadius: '0px',
              border: '0px'
            },
            '.MuiDataGrid-columnHeaderTitleContainer': {
              color: '#fff',
              fontSize: '15px',
              padding: '10px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center'
            },
            '.MuiDataGrid-cell': {
              border: '1px solid #fc7f03',
              padding: '5px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center'
            },
            '.MuiDataGrid-footerContainer': {
              border: '1px solid #fc7f03'
            },
            '.MuiDataGrid-footerContainer p': {
              margin: '0px'
            },
            '.MuiDataGrid-columnHeaderTitle': {
              overflow: 'visible',
              lineHeight: '1.43rem',
              whiteSpace: 'normal'
            }
          }}
          pageSize={rows.length}
          rows={rows}
          columns={columns}
          getRowHeight={() => 'auto'}
          disableSelectionOnClick
          autoHeight
          density='comfortable'
          headerHeight={70}
          hideFooter
        />
        <Button
          variant='contained'
          color='warning'
          style={{
            borderRadius: '0px',
            boxShadow: 'none'
          }}
          disabled={!(rows.length < count)}
          fullWidth
          onClick={() => onLoadMoreClick(filterRequestString)}
          size='large'
        >
          {translate('customDataGridLoadMore')}
        </Button>
      </div>
    </>
  )
}

export default OrganizersDataGrid
